import { useContext, useState } from 'react'
import { WppTypography } from '@wppopen/components-library-react'
import BackNavigationButton from 'components/BackNavigationButton/BackNavigationButton'
import { CreateAndEditRegionForm } from 'components/Forms'
import Banner from 'components/Banner'
import appContext from 'context/appContext'
import useApi from 'hooks/useApi'
import usePermissions from 'hooks/usePermissions'
import NotFound404Page from 'pages/NotFound404Page/NotFound404Page'
import { createCsApiUrlFromEndpoint } from 'utils'
import { useNavigate } from 'react-router-dom'
import SettingsPageContext from 'context/settingsPageContext/settingsPageContext'
import { FormSkeleton } from 'components/Skeletons'
import type { RegionsDetailData } from '@contracts'
import type { RegionsCreatePayloadV5 } from 'utils/utils.d'
import formStyles from '../FormView.module.scss'
import navigationHeaderStyles from '../NavigationHeader.module.scss'
import useInitialiser from 'hooks/useInitialiser'

const CreateRegionView = () => {
  const navigate = useNavigate()
  const { setActiveBannerId, setToastDetailsContext } = useContext(SettingsPageContext)
  const { configApi } = useContext(appContext)
  const { data: configData } = configApi
  const createRegionApiMethods = useApi<RegionsDetailData>(createCsApiUrlFromEndpoint('regions'))
  const { fetchData: postRegion, isLoading: isPostRegionLoading, url: postRegionUrl } = createRegionApiMethods
  const [createRegionError, setCreateRegionError] = useState<string>()

  const { clientsWithPermissions } = usePermissions({
    permissionSubset: ['PDP_APP_POST_REGIONS'],
  })

  const { isInitialising } = useInitialiser({
    subscribedApis: [configApi],
  })

  const [clientsWithPostRegion] = clientsWithPermissions

  const onSubmitForm = async (data: RegionsCreatePayloadV5) => {
    setCreateRegionError(undefined)
    const uploadNewRegion = async () => {
      try {
        // POST new region data
        const response = await postRegion(postRegionUrl, {
          method: 'POST',
          body: JSON.stringify(data),
        })
        const newRegionId = response._id
        if (!newRegionId) throw new Error('Error creating new region')
        setActiveBannerId(newRegionId)
        setToastDetailsContext({
          type: 'success',
          message: 'Region created successfully',
        })
        return newRegionId
      } catch (error) {
        const err = error as Error
        setCreateRegionError(err.message || 'something went wrong')
        window.scrollTo(0, 0)
        return Promise.reject(err)
      }
    }

    // solve region data upload promise
    return new Promise<string>((resolve, reject) => {
      uploadNewRegion()
        .then(result => {
          resolve(result)
        })
        .catch(error => {
          reject(error)
        })
    })
  }

  if (isInitialising) return <FormSkeleton />
  if (!isInitialising && !clientsWithPostRegion?.length) return <NotFound404Page />

  return (
    <>
      <div className={navigationHeaderStyles.navigationHeader__container}>
        <BackNavigationButton href="/settings/regions" text="Back to settings" isBottomMargin={false} />
        {createRegionError && (
          <Banner
            text={createRegionError}
            type="error"
            shouldBannerShow={!!createRegionError}
            onClose={() => setCreateRegionError(undefined)}
          />
        )}
      </div>

      <div className={formStyles.container}>
        {!!clientsWithPostRegion?.length && (
          <WppTypography type="3xl-heading" tag="h1">
            Add new region
          </WppTypography>
        )}
        <CreateAndEditRegionForm
          onSubmitForm={onSubmitForm}
          onCancelForm={() => navigate('/settings/regions')}
          isApiLoading={isPostRegionLoading}
          configRetailers={configData?.retailers}
        />
      </div>
    </>
  )
}

export default CreateRegionView
